<template>
  <div class="min-h-screen bg-cover bg-bottom fixed top-0 w-screen h-screen overflow-scroll"
    :style="page?.image ? { backgroundImage: `url(${page.image})` } : {}">
    <UPage class="bg-black/50 ">
      <UContainer>
      <div class="flex justify-between flex-col md:flex-row w-full py-8" >
        <AuthLoginForm
          class="backdrop-blur-sm transition-all duration-700 p-8 mb-6 md:mb-0 sm:p-16 border border-white/30 rounded-md max-w-md w-full" />
      
        <div
          v-if="!auth.isAuthenticated"
          class="flex-1 mb-auto backdrop-blur-sm transition-all duration-700 p-8 sm:p-16 border border-white/30 rounded-md max-w-md w-full text-left flex flex-col gap-3"
        >
            <div class="text-white text-2xl font-semibold uppercase text-center">New user?</div>
            <p class="text-xs mb-4 text-white/80 text-center">Enter your email to receive a link to set your password.</p>
            <form class="flex flex-col gap-3 items-center" @submit.prevent="onRegister">
              <UInput
                v-model="registerEmail"
                type="email"
                placeholder="E-mail"
                autocomplete="email"
                required
              />
              <UInput
                v-model="registerDisplayName"
                type="text"
                placeholder="Name (optional)"
                autocomplete="name"
              />
              
              <UButton
                class="cursor-pointer"
                icon="i-lucide-user-plus"
                type="submit"
                :loading="loadingRegister"
                :disabled="loadingRegister || !registerEmail"
              >
                Register & get password link
              </UButton>
            </form>
            <div v-if="registerMessage" class="text-xs text-center px-3 py-2 rounded bg-white/10 text-white">
              {{ registerMessage }}
            </div>
        </div>
      </div>
      </UContainer>
    </UPage>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AuthLoginForm from '@/components/auth/LoginForm.vue'
import { useAuthStore } from '@/store/auth'

definePageMeta({
  layout: 'headless'
})

const auth = useAuthStore()
const registerEmail = ref('')
const registerDisplayName = ref('')
const registerMessage = ref<string | null>(null)
const loadingRegister = ref(false)

async function onRegister() {
  registerMessage.value = null
  loadingRegister.value = true
  try {
    const response = await $fetch<{ success: boolean; email: string }>('/api/public-register', {
      method: 'POST',
      body: {
        email: registerEmail.value,
        displayName: registerDisplayName.value
      }
    })
    if (response.success) {
      registerMessage.value = `Check ${response.email} for a link to set your password.`
    }
  } catch (e: any) {
    registerMessage.value = e?.data?.message || readable(e)
  } finally {
    loadingRegister.value = false
  }
}

function readable(e: any) {
  const code = e?.code || ''
  switch (code) {
    case 'auth/invalid-email': return 'Invalid e-mail'
    case 'auth/wrong-password': return 'Bad password'
    case 'auth/user-not-found': return 'User not found'
    case 'auth/email-already-in-use': return 'E-mail already in use'
    case 'auth/weak-password': return 'Too weak password'
    case 'auth/popup-closed-by-user': return 'Cancelled log-in'
    case 'auth/missing-email': return 'Enter your e-mail above and click again.'
  }
  return e?.message || 'En error occurred'
}
const route = useRoute()

const { data: page } = await useAsyncData(route.path, () => {
  return queryCollection('pages').path(route.path).first()
})

useHead(() => ({
  title: page.value?.title || '7Kudo Sales demo – Login',
  meta: [
    {
      name: 'description',
      content: page.value?.description || 'Kudo Sales demo log in.',
    },
  ],
}))
</script>

<style scoped>
@reference "../assets/css/main.css";

:deep(h1) {
  @apply text-4xl font-bold mb-4 uppercase;
}

:deep(h2) {
  @apply text-7xl font-semibold mb-32;
}
</style>
